import { useSession } from '../../session'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Check, ChevronLeft, ChevronRight, Download, Plus, Search, Wallet } from 'lucide-react'
import { listCustomerInvoices, postCustomerInvoice } from '../../api/sales'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { useToast } from '../../components/Toast.jsx'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { fmtDay, parseWallClock, startOfToday } from '../../utils/format'
import SalesTabs from './SalesTabs'
import { money, num, statusOf } from './salesShared'

const PAGE_SIZE = 15

const customerText = (row) => row?.customer_name || (row?.customer_id ? `Customer #${row.customer_id}` : 'No customer')
const invoiceNo = (row) => row.invoice_number || 'Draft'
const balance = (row) => num(row.balance_amount ?? row.total_amount)

/** What a person needs to know: draft, unpaid, overdue, paid or void. */
function stateOf(row) {
  const s = statusOf(row)
  if (s === 'void' || s === 'voided') return 'void'
  if (s === 'draft' || !s) return 'draft'
  if (s === 'paid' || balance(row) <= 0.004) return 'paid'
  const due = parseWallClock(row.due_date)
  return due && due < startOfToday() ? 'overdue' : 'unpaid'
}

/** Some of it has been paid or credited, but not all. */
const isPartPaid = (row) => statusOf(row) === 'partially paid' || num(row.paid_to_date) + num(row.credited_to_date) > 0.004

const STATE = {
  draft: { label: 'Draft', tone: 'muted' },
  unpaid: { label: 'Unpaid', tone: 'info' },
  overdue: { label: 'Overdue', tone: 'warn' },
  paid: { label: 'Paid', tone: 'good' },
  void: { label: 'Void', tone: 'bad' },
}

/** The badge: unpaid and overdue invoices also say when part is paid. */
function badgeOf(row) {
  const st = stateOf(row)
  const partial = (st === 'unpaid' || st === 'overdue') && isPartPaid(row)
  if (st === 'overdue') return { label: partial ? 'Overdue, part paid' : 'Overdue', tone: 'warn' }
  if (st === 'unpaid' && partial) return { label: 'Part paid', tone: 'info' }
  return STATE[st]
}

const owing = (st) => st === 'unpaid' || st === 'overdue'
const FILTERS = [
  { id: 'all', label: 'All', match: () => true },
  { id: 'draft', label: 'Drafts', match: (row) => stateOf(row) === 'draft' },
  { id: 'unpaid', label: 'Unpaid', match: (row) => owing(stateOf(row)) },
  { id: 'partial', label: 'Part paid', match: (row) => owing(stateOf(row)) && isPartPaid(row) },
  { id: 'overdue', label: 'Overdue', match: (row) => stateOf(row) === 'overdue' },
  { id: 'paid', label: 'Paid', match: (row) => stateOf(row) === 'paid' },
  { id: 'void', label: 'Void', match: (row) => stateOf(row) === 'void' },
]

const cleanCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`

export default function CustomerInvoiceList() {
  const { has } = useSession()
  const nav = useNavigate()
  const { notify } = useToast()
  const [rows, setRows] = useState([])
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState('date')
  const [sortDir, setSortDir] = useState('desc')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [postingId, setPostingId] = useState(null)

  const refresh = async () => {
    setLoading(true)
    setError('')
    try {
      setRows(await listCustomerInvoices())
    } catch (e) {
      setRows([])
      setError(e.message || 'Could not load invoices.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { refresh() }, [])

  const counts = useMemo(() => Object.fromEntries(
    FILTERS.map((f) => [f.id, rows.filter((r) => f.match(r)).length])
  ), [rows])

  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter) || FILTERS[0]
    const term = search.trim().toLowerCase()
    const out = rows.filter((row) => f.match(row) && (!term || [
      row.invoice_number, row.invoice_id, customerText(row), row.memo,
    ].join(' ').toLowerCase().includes(term)))
    const key = (row) => {
      if (sortKey === 'customer') return customerText(row).toLowerCase()
      if (sortKey === 'date') return parseWallClock(row.invoice_date)?.getTime() || 0
      if (sortKey === 'due') return parseWallClock(row.due_date)?.getTime() || 0
      if (sortKey === 'total') return num(row.total_amount)
      if (sortKey === 'balance') return balance(row)
      return String(row.invoice_number || row.invoice_id || '')
    }
    out.sort((a, b) => {
      const av = key(a)
      const bv = key(b)
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv))
      return sortDir === 'asc' ? cmp : -cmp
    })
    return out
  }, [rows, search, sortKey, sortDir, filter])

  useEffect(() => { setPage(1) }, [search, filter, sortKey, sortDir])

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageSafe = Math.min(page, pageCount)
  const pageRows = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE)

  const setSort = (key) => {
    if (sortKey === key) setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortDir(['total', 'balance', 'date'].includes(key) ? 'desc' : 'asc')
    }
  }
  const sortHead = (key, label, className = '') => (
    <th className={`sortable ${className}`.trim()} onClick={() => setSort(key)} aria-sort={sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}>
      {label} <span>{sortKey === key ? (sortDir === 'asc' ? '↑' : '↓') : ''}</span>
    </th>
  )

  const postDraft = async (invoiceId) => {
    setPostingId(invoiceId)
    try {
      await postCustomerInvoice(invoiceId)
      notify('Invoice posted')
      await refresh()
    } catch (e) {
      notify(e.message || 'Could not post the invoice.')
    } finally {
      setPostingId(null)
    }
  }

  const exportCsv = () => {
    const header = ['Invoice', 'Customer', 'Date', 'Due', 'Status', 'Total', 'Paid', 'Credits', 'Balance due']
    const lines = filtered.map((row) => [
      invoiceNo(row),
      customerText(row),
      fmtDay(row.invoice_date),
      fmtDay(row.due_date),
      badgeOf(row).label,
      num(row.total_amount).toFixed(2),
      num(row.paid_to_date).toFixed(2),
      num(row.credited_to_date).toFixed(2),
      balance(row).toFixed(2),
    ].map(cleanCsv).join(','))
    const blob = new Blob([[header.map(cleanCsv).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'invoices.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="ci-page">
      <SalesTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Invoices</h1>
            <div className="ci-title-meta">Bills you send to customers who pay later.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          {has('invoices') ? <Link className="ci-btn ci-btn-primary" to="/sales/customer-invoices/new"><Plus size={15} />New invoice</Link> : <Link className="ci-btn ci-btn-primary" to="/jobs"><Plus size={15} />Start from a job</Link>}
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search invoices">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Invoice number or customer…" />
        </label>
        <div className="ci-toolbar-right">
          <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={exportCsv} disabled={!filtered.length}>
            <Download size={14} />
            Export
          </button>
        </div>
      </div>

      <FilterChips options={FILTERS.map((f) => ({ id: f.id, label: f.label, count: counts[f.id] }))} value={filter} onChange={setFilter} />

      {error && <ErrorBanner message={error} />}

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table">
            <thead>
              <tr>
                {sortHead('num', 'Invoice')}
                {sortHead('customer', 'Customer')}
                {sortHead('date', 'Date')}
                {sortHead('due', 'Due')}
                <th className="center">Status</th>
                {sortHead('total', 'Total', 'num')}
                {sortHead('balance', 'Balance due', 'num')}
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8"><Spinner label="Loading invoices…" /></td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan="8"><div className="ci-empty">{rows.length ? 'No invoices match.' : 'No invoices yet.'}</div></td></tr>
              ) : pageRows.map((row) => {
                const st = stateOf(row)
                return (
                  <tr key={row.invoice_id} className="row-link" onClick={() => nav(`/sales/customer-invoices/${row.invoice_id}`)}>
                    <td><Link className="ci-je-link" to={`/sales/customer-invoices/${row.invoice_id}`} onClick={(e) => e.stopPropagation()}>{invoiceNo(row)}</Link></td>
                    <td className="ci-strong">{customerText(row)}</td>
                    <td className="ci-muted-cell">{fmtDay(row.invoice_date)}</td>
                    <td className={st === 'overdue' ? 'ci-overdue-cell' : 'ci-muted-cell'}>{fmtDay(row.due_date) || '–'}</td>
                    <td className="center"><StatusBadge label={badgeOf(row).label} tone={badgeOf(row).tone} /></td>
                    <td className="num">{money(row.total_amount)}</td>
                    <td className="num ci-total">{st === 'draft' || st === 'void' ? '–' : money(balance(row))}</td>
                    <td className="num" onClick={(e) => e.stopPropagation()}>
                      {st === 'draft' && (
                        <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => postDraft(row.invoice_id)} disabled={postingId === row.invoice_id}>
                          <Check size={13} />
                          {postingId === row.invoice_id ? 'Posting…' : 'Post'}
                        </button>
                      )}
                      {(st === 'unpaid' || st === 'overdue') && (
                        <Link className="ci-btn ci-btn-ghost ci-btn-sm" to={`/sales/customer-payments?customer_id=${encodeURIComponent(row.customer_id)}&invoice_id=${encodeURIComponent(row.invoice_id)}`}>
                          <Wallet size={13} />
                          Record payment
                        </Link>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">{filtered.length} {filtered.length === 1 ? 'invoice' : 'invoices'}</span>
          {pageCount > 1 && (
            <div className="ci-page-buttons">
              <button type="button" className="ci-page-btn" disabled={pageSafe === 1} onClick={() => setPage(pageSafe - 1)}><ChevronLeft size={13} />Previous</button>
              <span className="ci-page-info">Page {pageSafe} of {pageCount}</span>
              <button type="button" className="ci-page-btn" disabled={pageSafe === pageCount} onClick={() => setPage(pageSafe + 1)}>Next<ChevronRight size={13} /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
